import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  asSavedSearchGroups,
  type SavedSearchGroupItem,
} from '@iconik-io/iconik-api/src/schema_patched/saved_search';
import type {
  SearchSavedSearchGroupSchemaWithId,
  SearchSavedSearchGroupSchemaWithIdNullable,
} from '@iconik-io/iconik-api/src/services/search';
import { OBJECT_TYPE_SAVED_SEARCH_GROUPS } from '@iconik-io/iconik-api/src/types/object_types';

import { updateObjectAtIndex } from '../../utils/helpers';
import { addObjectsType, addObjectType } from '../../utils/object_type';
import {
  getSavedSearchesNextPageSuccess,
  getSavedSearchesSuccess,
} from '../saved_search/actions';
import type { SavedSearchGroupsAppState } from './types';

const initialState: SavedSearchGroupsAppState = {
  groups: [],
  groupsSorted: [],
  expandedGroupIds: [],
  groupsPaginationState: {},
};

function compareId(
  a: SearchSavedSearchGroupSchemaWithIdNullable,
  b: SearchSavedSearchGroupSchemaWithIdNullable,
) {
  if (a.id < b.id) {
    return -1;
  }
  if (a.id > b.id) {
    return 1;
  }
  return 0;
}

export const savedSearchGroupsSlice = createSlice({
  name: 'saved_search_groups',
  initialState,
  reducers: {
    getSavedSearchGroupsSuccess: (
      state,
      action: PayloadAction<SearchSavedSearchGroupSchemaWithIdNullable[]>,
    ) => {
      const groups = addObjectsType(
        asSavedSearchGroups(action.payload),
        OBJECT_TYPE_SAVED_SEARCH_GROUPS,
      );
      state.groups = groups;
      state.groupsSorted = [...groups].sort(compareId);
    },
    createSavedSearchGroupSuccess: (
      state,
      action: PayloadAction<SearchSavedSearchGroupSchemaWithId>,
    ) => {
      const groups = state.groups.concat(
        addObjectType(action.payload, OBJECT_TYPE_SAVED_SEARCH_GROUPS),
      );
      state.groups = groups;
      state.groupsSorted = [...groups].sort(compareId);
    },
    // These two actions only affect the saved_search slice, which reduces
    // them in its extraReducers; here they exist just to create the actions.
    changeSavedSearchGroupSuccess: (
      _state,
      _action: PayloadAction<{ savedSearchId: string; groupId: string }>,
    ) => {},
    clearSavedSearchGroupSuccess: (
      _state,
      _action: PayloadAction<string>,
    ) => {},
    updateGroupNameSuccess: (
      state,
      action: PayloadAction<SearchSavedSearchGroupSchemaWithId>,
    ) => {
      const index = state.groups.findIndex(
        (group) => group.id === action.payload.id,
      );
      const groups = updateObjectAtIndex(state.groups, index, {
        name: action.payload.name,
      });
      state.groups = groups;
      state.groupsSorted = [...groups].sort(compareId);
    },
    deleteGroupSuccess: (state, action: PayloadAction<string>) => {
      const groupId = action.payload;
      const groups = state.groups.filter((group) => group.id !== groupId);
      state.groups = groups;
      state.groupsSorted = [...groups].sort(compareId);
    },
    expandGroup: (state, action: PayloadAction<SavedSearchGroupItem>) => {
      state.expandedGroupIds.push(action.payload.id);
    },
    collapseGroup: (state, action: PayloadAction<SavedSearchGroupItem>) => {
      state.expandedGroupIds = state.expandedGroupIds.filter(
        (id) => id !== action.payload.id,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSavedSearchesSuccess, (state, action) => {
        if (action.payload.groupId) {
          state.groupsPaginationState[action.payload.groupId] = {
            savedSearchesPage: action.payload.data.page,
            savedSearchesPages: action.payload.data.pages,
          };
        } else {
          const groupIds = action.payload.data.objects
            .map((item) => item.group_id)
            .filter((id): id is string => Boolean(id));
          state.expandedGroupIds = action.payload.query
            ? state.expandedGroupIds.concat(groupIds)
            : [];
        }
      })
      .addCase(getSavedSearchesNextPageSuccess, (state, action) => {
        if (action.payload.groupId) {
          state.groupsPaginationState[action.payload.groupId] = {
            savedSearchesPage: action.payload.data.page,
            savedSearchesPages: action.payload.data.pages,
          };
        }
      });
  },
});

export const {
  getSavedSearchGroupsSuccess,
  createSavedSearchGroupSuccess,
  changeSavedSearchGroupSuccess,
  clearSavedSearchGroupSuccess,
  updateGroupNameSuccess,
  deleteGroupSuccess,
  expandGroup,
  collapseGroup,
} = savedSearchGroupsSlice.actions;

export const savedSearchGroupsReducer = savedSearchGroupsSlice.reducer;
